/**
 * Locale → emergency-services phone number.
 *
 * Looks up by the country part of `navigator.language` (e.g. `de-DE` → `DE`,
 * `en-US` → `US`). Falls back to 112 — the EU's emergency number, also
 * recognised in most of Asia, Africa, and South America, so it's a safer
 * default than 911.
 *
 * The visible string is what we render on the call button. The `tel:` URI is
 * what we wire to the anchor `href`. Some countries' formats include the
 * leading 0 for landline contexts but emergency numbers are short and
 * country-specific — we keep them as the canonical short form.
 */

export type EmergencyService = {
  displayNumber: string;
  telUri: string;
  /** The two-letter country code we matched against. Useful for analytics. */
  region: string;
};

const NUMBERS: Record<string, string> = {
  // EU
  DE: '112', AT: '112', CH: '112', NL: '112', BE: '112', LU: '112',
  FR: '112', ES: '112', IT: '112', PT: '112', PL: '112', CZ: '112',
  SK: '112', HU: '112', SE: '112', NO: '112', DK: '112', FI: '112',
  IE: '112', GR: '112', RO: '112', BG: '112', HR: '112', SI: '112',
  EE: '112', LV: '112', LT: '112', IS: '112',
  // UK + dependencies
  GB: '999', JE: '999', GG: '999', IM: '999',
  // North America
  US: '911', CA: '911',
  // Oceania
  AU: '000', NZ: '111',
  // Rest of the world (selective; falls back to 112 otherwise)
  JP: '110',
  KR: '112',
  ZA: '10111',
  BR: '190',
  MX: '911',
  IN: '112',
  TR: '112',
  RU: '112',
};

const DEFAULT_NUMBER = '112';
const DEFAULT_REGION = 'XX';

export function getEmergencyService(locale: string = navigator.language): EmergencyService {
  const region = extractRegion(locale);
  const number = (region && NUMBERS[region]) || DEFAULT_NUMBER;
  return {
    displayNumber: number,
    telUri: `tel:${number}`,
    region: region ?? DEFAULT_REGION,
  };
}

function extractRegion(locale: string): string | null {
  if (!locale) return null;
  // Accepts `de`, `de-DE`, `de_DE`, `de-Latn-DE`. Returns 'DE' or null.
  const parts = locale.replace(/_/g, '-').split('-');
  for (let i = 1; i < parts.length; i++) {
    const segment = parts[i] ?? '';
    if (segment.length === 2 && /^[A-Za-z]{2}$/.test(segment)) {
      return segment.toUpperCase();
    }
  }
  // Last-ditch: a single-segment locale like `de` — assume the language matches the country.
  const language = (parts[0] ?? '').toLowerCase();
  if (language.length === 2 && /^[a-z]{2}$/.test(language)) {
    return language.toUpperCase();
  }
  return null;
}
